import { useMutation } from "@tanstack/react-query";

import AuthClient from "../clients/AuthClient";
import { useApi } from "./useApi";
import { useAuth } from "./useAuth";

export interface Login {
  email: string;
  password: string;
  offering_id?: string;
  bcode?: string | null;
  ref?: string | null;
}

export interface MFA {
  email: string;
  mfa_code: number;
  offering_id?: string;
  bcode?: string | null;
  ref?: string | null;
}

export interface Register {
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  phone?: string;
  company?: string;
  entity_type: string;
  accept_tos?: Date;
  accept_privacy?: Date;
  accept_sms_email?: Date;
  token?: string;
}

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useLogin = (options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(async (data: Login) => {
    return authApi.login(data);
  }, options);
};

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useMFACode = (options = {}) => {
  const authApi = useApi(AuthClient);
  const { setTokens } = useAuth();

  return useMutation(async (data: MFA) => {
    const res = await authApi.mfa(data);
    setTokens(res.tokens);
    return res;
  }, options);
};

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useLoginIssuer = (options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(async (data: { email: string; password: string }) => {
    return authApi.loginIssuer(data);
  }, options);
};

/**
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useMfaSms = (options = {}) => {
  const authApi = useApi(AuthClient);
  const { setTokens } = useAuth();

  return useMutation(async (data: { email: string; mfa_code: string }) => {
    const res = await authApi.mfaSms(data);
    setTokens(res.tokens);
    return res;
  }, options);
};

export const useRegister = (type: "issuer" | "investor", token = "", options = {}) => {
  const authApi = useApi(AuthClient);
  const { setTokens } = useAuth();

  return useMutation(async (data: Register) => {
    const res = await authApi.register(data, type, token);
    setTokens(res.tokens);
    return res;
  }, options);
};

export const useRegistrationContext = (token: string, options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(async () => authApi.registrationContext(token), options);
};

export const useRegistrationMfa = (token: string, options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(async () => authApi.registrationMfa(token), options);
};

export const useRegistrationLogin = (token: string, options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(async (password: string) => authApi.registrationLogin(token, password), options);
};

export const useRegistrationVerify = (token: string, options = {}) => {
  const authApi = useApi(AuthClient);
  const { setTokens } = useAuth();

  return useMutation(async (mfa_code: string) => {
    const res = await authApi.registrationVerify(token, mfa_code);
    setTokens(res.tokens);
    return res;
  }, options);
};

export const useRegistrationSetup = (token: string, options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(
    async (data: { password: string; password_confirmation: string }) =>
      authApi.registrationSetup(token, { ...data, accept_tos: true }),
    options,
  );
};

export const useInvitationLogin = (inviteToken: string, options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(async (data: Login) => {
    return authApi.invitationLogin(data, inviteToken);
  }, options);
};

export const useWorkflowRegister = (offeringId: string, options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation(async (data: Register) => {
    const res = await authApi.workflowRegister(data, offeringId);
    return res;
  }, options);
};

export const useWorkflowLogin = (offeringId: string, options = {}) => {
  const authApi = useApi(AuthClient);

  return useMutation((data: Login) => authApi.login({ ...data, offering_id: offeringId }), options);
};

export const useRefreshToken = (options = {}) => {
  const authApi = useApi(AuthClient);
  const { setTokens } = useAuth();

  return useMutation(async (refreshToken?: string | null) => {
    if (!refreshToken) return null;

    const res = await authApi.refreshToken(refreshToken);
    setTokens(res);
    return res;
  }, options);
};
